import { useState, useEffect } from "react";
import { useSearchParams, useParams } from "react-router-dom";
import { invokeEdgeFunction } from "@/lib/edge-functions";
import { HandleSupplierResponseRequest } from "@/types/edge-functions";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { Textarea } from "@/components/ui/textarea";
import {
  CheckCircle,
  XCircle,
  Loader2,
  AlertCircle,
  Calendar,
  Zap,
  Building2,
  User,
  FileText,
  ExternalLink,
  MessageSquare,
  Send,
} from "lucide-react";

const COUNTER_OFFER_PROMPTS = [
  "I'd accept at a lower discount rate — can we discuss terms?",
  "Could we apply this discount to all our outstanding invoices?",
  "We'd accept for a shorter payment window than quoted.",
  "Can we set up auto-accept for future invoices above this amount?",
];

type InvoiceStatus =
  | "pending"
  | "early_payment_scheduled"
  | "early_payment_completed"
  | "standard_payment";

interface Invoice {
  id: string;
  supplier_name: string;
  supplier_email: string;
  payment_amount: number;
  discounted_payment_amount: number | null;
  discount_amount: number | null;
  payment_due_date: string;
  apr_rate: number;
  status: InvoiceStatus;
  invoice_file_url: string | null;
  invoice_file_name: string | null;
}

interface Settings {
  buyer_name: string;
}

export default function SupplierResponse() {
  const [searchParams] = useSearchParams();
  const { invoiceId: paramInvoiceId } = useParams();
  const invoiceId = paramInvoiceId || searchParams.get("invoiceId");

  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAccepting, setIsAccepting] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submittedAction, setSubmittedAction] = useState<"accept" | "reject" | null>(null);
  const [showCounterOffer, setShowCounterOffer] = useState(
    searchParams.get("counterOffer") === "1"
  );
  const [counterMessage, setCounterMessage] = useState("");
  const [counterSent, setCounterSent] = useState(false);
  const [isSendingCounter, setIsSendingCounter] = useState(false);

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!invoiceId) {
        setError("Missing invoice ID");
        setIsLoading(false);
        return;
      }

      try {
        // Fetch invoice and settings in parallel
        const [invoiceResult, settingsResult] = await Promise.all([
          supabase.from("invoices").select("*").eq("id", invoiceId).single(),
          supabase.from("settings").select("buyer_name").limit(1).single(),
        ]);

        if (invoiceResult.error || !invoiceResult.data) {
          setError("Invoice not found");
          return;
        }

        setInvoice(invoiceResult.data as Invoice);

        if (settingsResult.data) {
          setSettings(settingsResult.data as Settings);
        }

        // If already processed, show the result
        if (invoiceResult.data.status !== "pending") {
          setSubmitted(true);
          const s = invoiceResult.data.status as string;
          setSubmittedAction(
            s === "early_payment_scheduled" || s === "early_payment" ? "accept" : "reject"
          );
        }
      } catch (_err) {
        setError("Failed to load invoice");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [invoiceId]);

  const handleCounterOffer = async () => {
    if (!counterMessage.trim() || !invoice) return;
    setIsSendingCounter(true);
    // In production this would call a send-counter-offer edge function.
    // For now we simulate a short delay and show a success state.
    await new Promise((r) => setTimeout(r, 800));
    setIsSendingCounter(false);
    setCounterSent(true);
  };

  const handleResponse = async (responseAction: "accept" | "reject") => {
    if (!invoice) return;

    if (responseAction === "accept") {
      setIsAccepting(true);
    } else {
      setIsDeclining(true);
    }
    setError(null);

    try {
      const request: HandleSupplierResponseRequest = {
        invoiceId: invoice.id,
        action: responseAction,
      };

      await invokeEdgeFunction("handle-supplier-response", request, {
        strategy: "linear",
        maxRetries: 2,
        retryDelay: 200,
        idempotent: true,
      });

      setSubmitted(true);
      setSubmittedAction(responseAction);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to process response");
    } finally {
      setIsAccepting(false);
      setIsDeclining(false);
    }
  };

  const buyerName = settings?.buyer_name || "Your Buyer";

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted flex items-center justify-center p-4">
        <Card className="p-12 text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading offer details...</p>
        </Card>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted flex items-center justify-center p-4">
        <Card className="p-12 text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-destructive" />
          </div>
          <h1 className="text-2xl font-bold mb-2">Error</h1>
          <p className="text-muted-foreground">{error || "Failed to load invoice offer"}</p>
        </Card>
      </div>
    );
  }

  if (submitted) {
    const isAccepted = submittedAction === "accept";
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted flex items-center justify-center p-4">
        <Card className="p-12 text-center max-w-md">
          <div
            className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 ${
              isAccepted ? "bg-success" : "bg-muted"
            }`}
          >
            {isAccepted ? (
              <CheckCircle className="w-10 h-10 text-white" />
            ) : (
              <XCircle className="w-10 h-10 text-muted-foreground" />
            )}
          </div>
          <h1 className="text-3xl font-bold mb-4">
            {isAccepted ? "Early Payment Accepted!" : "Offer Declined"}
          </h1>
          <p className="text-muted-foreground text-lg mb-6">
            {isAccepted
              ? `Thank you! You will receive ${formatCurrency(invoice.discounted_payment_amount || 0)} within 5 business days.`
              : `You have declined the early payment offer. The invoice will be paid in full (${formatCurrency(invoice.payment_amount)}) on ${formatDate(invoice.payment_due_date)}.`}
          </p>
          <div className="p-4 bg-muted rounded-lg">
            <p className="text-sm text-muted-foreground">
              {buyerName} has been notified of your decision.
            </p>
          </div>
          <div className="mt-6">
            <Button
              className="w-full"
              onClick={() =>
                (window.location.href = `/supplier?email=${encodeURIComponent(invoice.supplier_email)}`)
              }
            >
              <Zap className="w-4 h-4 mr-2" />
              Set Up Your DiscoRun Profile
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const savings = invoice.discount_amount || 0;

  // Days between today and the original due date
  const daysEarly = Math.max(
    0,
    Math.round(
      (new Date(invoice.payment_due_date).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
    )
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-lg">
              <Zap className="w-5 h-5 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-foreground">DiscoRun AI</h1>
              <p className="text-sm text-muted-foreground">Early Payment Offer</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8 max-w-2xl">
        <div className="space-y-6">
          {/* Parties Information */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Card className="p-4">
              <div className="flex items-center gap-3 mb-2">
                <Building2 className="w-5 h-5 text-primary" />
                <span className="text-sm font-medium text-muted-foreground">From (Buyer)</span>
              </div>
              <p className="text-lg font-semibold text-foreground">{buyerName}</p>
            </Card>
            <Card className="p-4">
              <div className="flex items-center gap-3 mb-2">
                <User className="w-5 h-5 text-primary" />
                <span className="text-sm font-medium text-muted-foreground">To (Supplier)</span>
              </div>
              <p className="text-lg font-semibold text-foreground">{invoice.supplier_name}</p>
              <p className="text-sm text-muted-foreground">{invoice.supplier_email}</p>
            </Card>
          </div>

          {/* Invoice Reference */}
          <Card className="p-4">
            <div className="flex items-center gap-3 mb-3">
              <FileText className="w-5 h-5 text-primary" />
              <span className="text-sm font-medium text-muted-foreground">Invoice Reference</span>
            </div>
            <div className="space-y-2">
              {invoice.invoice_file_name && (
                <p className="font-semibold text-foreground">{invoice.invoice_file_name}</p>
              )}
              <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                <span>
                  Amount:{" "}
                  <strong className="text-foreground">
                    {formatCurrency(invoice.payment_amount)}
                  </strong>
                </span>
                <span>
                  Due:{" "}
                  <strong className="text-foreground">
                    {formatDate(invoice.payment_due_date)}
                  </strong>
                </span>
              </div>
              {invoice.invoice_file_url && (
                <a
                  href={invoice.invoice_file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-primary hover:underline text-sm mt-2"
                >
                  <FileText className="w-4 h-4" />
                  View Invoice PDF
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </Card>

          {/* Early Payment Option */}
          <Card className="overflow-hidden border-2 border-success/30">
            <div className="bg-gradient-to-r from-success to-success/80 p-6 text-success-foreground text-center">
              <p className="text-sm opacity-90 mb-1">Accept Early Payment</p>
              <p className="text-4xl font-bold">
                {formatCurrency(invoice.discounted_payment_amount || 0)}
              </p>
              <p className="text-sm opacity-90 mt-2">
                Within 5 business days ({daysEarly} days early)
              </p>
            </div>
            <div className="p-6">
              <div className="space-y-3 mb-6">
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="text-muted-foreground">Original Invoice Amount</span>
                  <span className="font-semibold">{formatCurrency(invoice.payment_amount)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="text-muted-foreground">Early Payment Discount</span>
                  <span className="font-semibold text-success">-{formatCurrency(savings)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="text-muted-foreground">Discount Rate</span>
                  <span className="font-semibold">
                    {((savings / invoice.payment_amount) * 100).toFixed(2)}%
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="text-muted-foreground flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Early Payment Date
                  </span>
                  <span className="font-semibold text-success">
                    Within 5 business days ({daysEarly} days early)
                  </span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-muted-foreground flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Original Due Date
                  </span>
                  <span className="font-semibold">{formatDate(invoice.payment_due_date)}</span>
                </div>
              </div>
              <Button
                className="w-full bg-success hover:bg-success/90 text-success-foreground h-12 text-base"
                onClick={() => handleResponse("accept")}
                disabled={isAccepting || isDeclining}
              >
                {isAccepting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Accept Early Payment
                  </>
                )}
              </Button>
            </div>
          </Card>

          {/* Counter Offer */}
          <Card className="overflow-hidden border border-border/60">
            <button
              className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-muted/30 transition-colors"
              onClick={() => setShowCounterOffer((v) => !v)}
            >
              <div className="flex items-center gap-3">
                <MessageSquare className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium text-foreground">Make a Counter Offer</p>
                  <p className="text-xs text-muted-foreground">
                    Send a message to {buyerName} with your preferred terms
                  </p>
                </div>
              </div>
              {showCounterOffer ? (
                <XCircle className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              ) : (
                <MessageSquare className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              )}
            </button>

            {showCounterOffer && (
              <div className="border-t border-border/60 px-6 py-5 space-y-4">
                {counterSent ? (
                  <div className="text-center py-4">
                    <CheckCircle className="w-10 h-10 text-success mx-auto mb-3" />
                    <p className="font-semibold text-foreground">Counter offer sent!</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {buyerName} will review your message and follow up directly.
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Example prompts */}
                    <div className="flex flex-wrap gap-2">
                      {COUNTER_OFFER_PROMPTS.map((prompt) => (
                        <button
                          key={prompt}
                          onClick={() => setCounterMessage(prompt)}
                          className="text-xs px-3 py-1.5 rounded-full border border-border bg-muted/40 hover:bg-muted transition-colors text-muted-foreground hover:text-foreground text-left"
                        >
                          {prompt}
                        </button>
                      ))}
                    </div>

                    <Textarea
                      value={counterMessage}
                      onChange={(e) => setCounterMessage(e.target.value)}
                      placeholder="Describe your preferred terms or any questions about this offer..."
                      className="resize-none h-24 text-sm"
                    />

                    <Button
                      className="w-full"
                      onClick={handleCounterOffer}
                      disabled={!counterMessage.trim() || isSendingCounter}
                    >
                      {isSendingCounter ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Send Counter Offer to {buyerName}
                        </>
                      )}
                    </Button>
                  </>
                )}
              </div>
            )}
          </Card>

          {/* Standard Payment Option */}
          <Card className="p-6">
            <div className="text-center mb-4">
              <p className="text-sm text-muted-foreground mb-1">Or Wait for Full Payment</p>
              <p className="text-2xl font-bold text-foreground">
                {formatCurrency(invoice.payment_amount)}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Payment on {formatDate(invoice.payment_due_date)}
              </p>
            </div>
            <Button
              variant="outline"
              className="w-full h-12 text-base"
              onClick={() => handleResponse("reject")}
              disabled={isAccepting || isDeclining}
            >
              {isDeclining ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <XCircle className="w-4 h-4 mr-2" />
                  Decline & Wait for Full Payment
                </>
              )}
            </Button>
          </Card>

          {/* CTA for DiscoRun profile */}
          <Card className="p-6 text-center border-primary/20">
            <Zap className="w-6 h-6 text-primary mx-auto mb-2" />
            <p className="text-sm font-medium text-foreground mb-1">Make early payments easier</p>
            <p className="text-xs text-muted-foreground mb-3">
              Set up your DiscoRun profile to auto-accept offers and get paid faster.
            </p>
            <Button
              size="sm"
              onClick={() =>
                (window.location.href = `/supplier?email=${encodeURIComponent(invoice.supplier_email)}`)
              }
            >
              Set Up Profile with DiscoRun
            </Button>
          </Card>

          <p className="text-center text-sm text-muted-foreground">
            This offer expires in 48 hours. Questions? Contact {buyerName} directly.
          </p>
        </div>
      </main>
    </div>
  );
}
